import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { AiOutlineLoading } from 'react-icons/ai';
import { MdCheckCircleOutline, MdOutlineCancel, MdOpenInNew, MdRefresh } from 'react-icons/md';
import { TbPlugConnected } from 'react-icons/tb';
import ClientService from '../../../rest/clientService';
import CustomButton from '../../../components/lib/customButton';
import AsaasSeal from '../../../components/reusables/asaasSeal';
import Lottie from 'react-lottie';
import completedAnimation from '../../../resources/completed_animation2.json';

const COMPANY_TYPES = [
    { value: 'MEI', label: 'MEI' },
    { value: 'LTDA', label: 'Ltda.' },
    { value: 'SA', label: 'S.A.' },
    { value: 'INDIVIDUAL', label: 'Empresário Individual' },
    { value: 'ASSOCIATION', label: 'Associação' },
];

const StatusBadge = ({ status }) => {
    const map = {
        APPROVED: { label: 'Aprovado', cls: 'bg-green-100 text-green-700' },
        PENDING:  { label: 'Pendente', cls: 'bg-yellow-100 text-yellow-700' },
        REJECTED: { label: 'Rejeitado', cls: 'bg-red-100 text-red-700' },
        NONE:     { label: 'Não iniciado', cls: 'bg-gray-100 text-gray-500' },
    };
    const { label, cls } = map[status] ?? map.NONE;
    return (
        <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${cls}`}>
            {label}
        </span>
    );
};

export function IntegrationTab({ client }) {
    const [asaasStatus, setAsaasStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [showOnboardForm, setShowOnboardForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showSmtpModal, setShowSmtpModal] = useState(false);
    const [emailCnx, setEmailCnx] = useState(false);
    const [smtpEmail, setSmtpEmail] = useState('');
    const [smtpPassword, setSmtpPassword] = useState('');
    const [showSmtpAnimation, setShowSmtpAnimation] = useState(false);
    const [savingSmtp, setSavingSmtp] = useState(false);

    const [form, setForm] = useState({
        name: '',
        cpfCnpj: '',
        email: '',
        mobilePhone: '',
        address: '',
        addressNumber: '',
        province: '',
        city: '',
        postalCode: '',
        companyType: '',
        incomeValue: '',
    });

    const fetchStatus = async () => {
        setLoadingStatus(true);
        try {
            const data = await ClientService.getAsaasStatus();
            setAsaasStatus(data);
        } catch {
            setAsaasStatus({ onboardingStatus: 'NONE', onboardingUrl: null });
        } finally {
            setLoadingStatus(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        ClientService.checkIfSmtpIsConnected().then(setEmailCnx).catch(() => {});
    }, []);

    useEffect(() => {
        if (client) {
            setForm(prev => ({
                ...prev,
                name: client.client_name ?? '',
                cpfCnpj: client.cnpj ?? '',
                email: client.contactemail ?? '',
                mobilePhone: client.phone_number ?? '',
            }));
        }
    }, [client]);

    const handleOnboard = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const result = await ClientService.asaasOnboard({
                ...form,
                incomeValue: parseInt(form.incomeValue, 10) || 0,
            });
            setAsaasStatus(result);
            setShowOnboardForm(false);
            toast.success('Integração iniciada! Acesse o link para enviar os documentos.');
        } catch {
            toast.error('Erro ao iniciar integração. Verifique os dados e tente novamente.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleConnectSmtp = async () => {
        setSavingSmtp(true);
        try {
            await ClientService.saveSmtpConnection({ email: smtpEmail, token: smtpPassword });
            setShowSmtpAnimation(true);
            setTimeout(() => { setShowSmtpAnimation(false); setShowSmtpModal(false); setEmailCnx(true); }, 1500);
        } catch {
            toast.error('Erro ao salvar conexão SMTP.');
        } finally {
            setSavingSmtp(false);
        }
    };

    const field = (label, key, type = 'text', placeholder = '') => (
        <div>
            <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">{label}</label>
            <input
                type={type}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-custom_blue"
                value={form[key]}
                placeholder={placeholder}
                onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                required
            />
        </div>
    );

    const status = asaasStatus?.onboardingStatus ?? 'NONE';

    return (
        <div className="mx-4 md:mx-16 my-4 space-y-8">

            {/* ── Asaas / Carteira Digital ── */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-4">
                        <p className="text-xl text-custom_gray_font font-bold">Carteira Digital (Asaas)</p>
                        {status === 'APPROVED' && <AsaasSeal size="md" />}
                    </div>
                    {status !== 'NONE' && (
                        <button onClick={fetchStatus} className="text-gray-400 hover:text-custom_blue transition-colors" title="Atualizar status">
                            <MdRefresh size={20} />
                        </button>
                    )}
                </div>

                {loadingStatus ? (
                    <div className="flex items-center gap-2 text-gray-400">
                        <AiOutlineLoading className="animate-spin" /> Verificando status...
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-600">Status da integração</span>
                            <StatusBadge status={status} />
                        </div>

                        {status === 'NONE' && !showOnboardForm && (
                            <div>
                                <p className="text-sm text-gray-500 mb-3">
                                    Integre sua conta ao Asaas para aceitar pagamentos via PIX e cartão de crédito.
                                </p>
                                <CustomButton buttonType="confirm" showIcon={false} text="Iniciar Integração"
                                    onClick={() => setShowOnboardForm(true)} />
                            </div>
                        )}

                        {(status === 'NONE' || status === 'REJECTED') && showOnboardForm && (
                            <form onSubmit={handleOnboard} className="space-y-3 pt-2 border-t border-gray-100">
                                <p className="text-sm font-semibold text-gray-600">Dados para cadastro</p>
                                <div className="grid grid-cols-2 gap-3">
                                    {field('Nome da empresa', 'name')}
                                    {field('CNPJ / CPF', 'cpfCnpj')}
                                    {field('E-mail', 'email', 'email')}
                                    {field('Telefone', 'mobilePhone', 'tel', '(11) 99999-9999')}
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    {field('Endereço', 'address')}
                                    {field('Número', 'addressNumber')}
                                    {field('Bairro', 'province')}
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    {field('Cidade', 'city')}
                                    {field('CEP', 'postalCode')}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Tipo de empresa</label>
                                        <select
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-custom_blue"
                                            value={form.companyType}
                                            onChange={e => setForm(p => ({ ...p, companyType: e.target.value }))}
                                            required
                                        >
                                            <option value="">Selecione...</option>
                                            {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                                {field('Receita mensal estimada (R$)', 'incomeValue', 'number', '10000')}

                                <div className="flex gap-3 pt-2">
                                    <button type="submit" disabled={submitting}
                                        className="flex-1 bg-custom_blue text-white font-bold py-2.5 rounded-lg flex justify-center items-center gap-2 disabled:opacity-50">
                                        {submitting ? <AiOutlineLoading className="animate-spin" /> : <TbPlugConnected />}
                                        {submitting ? 'Enviando...' : 'Confirmar Integração'}
                                    </button>
                                    <button type="button" onClick={() => setShowOnboardForm(false)}
                                        className="px-5 py-2.5 bg-gray-100 text-gray-600 font-bold rounded-lg">
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        )}

                        {status === 'PENDING' && (
                            <div className="space-y-3">
                                <p className="text-sm text-gray-500">
                                    Sua conta está em análise. Acesse o link abaixo para enviar os documentos necessários.
                                </p>
                                {asaasStatus?.onboardingUrl && (
                                    <a
                                        href={asaasStatus.onboardingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-custom_blue font-bold text-sm underline"
                                    >
                                        <MdOpenInNew /> Acessar portal de documentos
                                    </a>
                                )}
                            </div>
                        )}

                        {status === 'APPROVED' && (
                            <p className="text-sm text-green-600 font-semibold flex items-center gap-2">
                                <MdCheckCircleOutline size={18} /> Conta aprovada. Pagamentos via PIX e cartão estão ativos.
                            </p>
                        )}

                        {status === 'REJECTED' && !showOnboardForm && (
                            <div className="space-y-3">
                                <p className="text-sm text-red-500">
                                    Sua solicitação foi rejeitada. Revise os dados e tente novamente.
                                </p>
                                <CustomButton buttonType="confirm" showIcon={false} text="Tentar novamente"
                                    onClick={() => setShowOnboardForm(true)} />
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* ── SMTP ── */}
            <section>
                <p className="text-xl text-custom_gray_font font-bold mb-3">Servidor de E-mail (SMTP)</p>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-600">Status da conexão</span>
                        <StatusBadge status={emailCnx ? 'APPROVED' : 'NONE'} />
                    </div>
                    {emailCnx ? (
                        <p className="mt-3 text-sm text-green-600 font-semibold flex items-center gap-2">
                            <MdCheckCircleOutline size={18} /> Servidor SMTP conectado.
                        </p>
                    ) : (
                        <div className="mt-3">
                            <p className="text-sm text-gray-500 mb-3">Conecte um servidor SMTP para envio de e-mails automáticos.</p>
                            <CustomButton buttonType="confirm" showIcon={false} text="Conectar SMTP"
                                onClick={() => setShowSmtpModal(true)} />
                        </div>
                    )}
                </div>
            </section>

            {/* ── SMTP Modal ── */}
            {showSmtpModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md">
                        {showSmtpAnimation ? (
                            <Lottie options={{ animationData: completedAnimation }} height={150} width={150} />
                        ) : (
                            <>
                                <h3 className="text-lg font-bold text-gray-800 mb-6">Conexão SMTP</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Endereço de E-mail</label>
                                        <input type="email" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                            value={smtpEmail} onChange={e => setSmtpEmail(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Senha de Aplicativo</label>
                                        <input type="text" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                            value={smtpPassword} onChange={e => setSmtpPassword(e.target.value)} />
                                    </div>
                                </div>
                                <div className="flex gap-3 mt-6">
                                    <button onClick={handleConnectSmtp} disabled={savingSmtp}
                                        className="flex-1 bg-custom_green text-white font-bold py-2.5 rounded-lg flex justify-center items-center gap-2 disabled:opacity-50">
                                        {savingSmtp ? <AiOutlineLoading className="animate-spin" /> : <MdCheckCircleOutline />}
                                        Salvar
                                    </button>
                                    <button onClick={() => setShowSmtpModal(false)}
                                        className="flex-1 bg-custom_red text-white font-bold py-2.5 rounded-lg flex justify-center items-center gap-2">
                                        <MdOutlineCancel /> Cancelar
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
